<template>
  <div ref="mapScrollContainer" class="world-map-container">
    <svg
      v-if="svgPaths.length > 0"
      viewBox="0 0 2000 857"
      class="world-map-svg"
    >
      <g id="features">
        <path
          v-for="path in svgPaths"
          :key="path.id"
          :id="path.id"
          :d="path.d"
          :fill="getCountryFillColor(path.id)"
          :stroke="getCountryStroke(path.id)"
          stroke-width="0.5"
          class="country-path"
          @mouseenter="handleCountryHover(path.id, $event)"
          @mousemove="updatePopupPosition($event)"
          @mouseleave="handleCountryLeave"
          @click="handleCountryClick(path.id)"
        />
      </g>
    </svg>

    <!-- ホバー時のポップアップ -->
    <div
      v-if="hoveredCountry && shioriByCountry[hoveredCountry]"
      class="country-popup"
      :style="{
        left: `${popupPosition.x + 15}px`,
        top: `${popupPosition.y + 15}px`,
      }"
      @mouseenter="keepPopupOpen"
      @mouseleave="handlePopupLeave"
    >
      <div class="popup-header">
        {{ getCountryName(hoveredCountry) }}
      </div>
      <div class="popup-content">
        <div
          v-for="shiori in shioriByCountry[hoveredCountry]"
          :key="shiori.SHIORI_NOV"
          class="popup-shiori-item"
          @click="emitShioriClick(shiori)"
        >
          <div class="popup-shiori-title">{{ shiori.SUBJECT_NMV }}</div>
          <div class="popup-shiori-date">
            {{ formatDate(shiori.TRIP_FROM) }} 〜
            {{ formatDate(shiori.TRIP_TO) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted } from "vue";
import {
  getISOCode,
  getISOCodeFromEnglish,
  getJapaneseName,
  getEnglishName,
} from "../data/countryMapping";
import { useDestinationMasterStore } from "@/store/destination-master-store.js";

export default {
  name: "WorldMapSVG",
  props: {
    shioriList: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["shiori-click"],
  setup(props, { emit }) {
    const svgPaths = ref([]);
    const mapScrollContainer = ref(null);
    const hoveredCountry = ref(null);
    const popupPosition = ref({ x: 0, y: 0 });
    const isPopupHovered = ref(false); // ポップアップ上にマウスがあるかどうか
    const destMasterStore = useDestinationMasterStore();

    /**
     * world.svgファイルをfetchしてpathデータを抽出
     * id属性とclass属性の両方を取得（アメリカ、ロシア、カナダなどは複数pathに分かれている）
     */
    const parseSVGPaths = async () => {
      try {
        const response = await fetch("/world.svg");
        const svgText = await response.text();

        // DOMParserでSVGをパース
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");

        // すべてのpath要素を取得（idまたはclassを持つもの）
        const paths = svgDoc.querySelectorAll("path");

        svgPaths.value = Array.from(paths)
          .filter(
            (path) => path.getAttribute("id") || path.getAttribute("class"),
          )
          .map((path, index) => {
            const id = path.getAttribute("id");
            const className = path.getAttribute("class");
            const name = path.getAttribute("name");

            // id属性があればそれを使用、なければclass名からISO国コードを取得
            let countryId = id;
            if (!countryId && className) {
              // 英語国名からISO国コードに変換
              countryId = getISOCodeFromEnglish(className) || className;
            }

            return {
              id: countryId || `path-${index}`,
              d: path.getAttribute("d"),
              name: name || className || countryId,
              className: className, // デバッグ用
            };
          });

        console.log(
          `WorldMapSVG: ${svgPaths.value.length} 個のパスを読み込みました`,
        );
        await centerMapScroll();
      } catch (error) {
        console.error("world.svgの読み込みに失敗しました:", error);
      }
    };

    const centerMapScroll = async () => {
      await nextTick();
      const container = mapScrollContainer.value;
      if (!container) return;
      container.scrollLeft = Math.max(
        0,
        (container.scrollWidth - container.clientWidth) / 2,
      );
    };

    /**
     * しおりデータを国コード（ISO）でグループ化
     * DESTINATIONSフィールド（JSON配列）から国名を取得し、ISO国コードに変換
     */
    const shioriByCountry = computed(() => {
      const grouped = {};

      props.shioriList.forEach((shiori) => {
        try {
          // DESTINATIONSフィールドをパース
          const destinations = shiori.DESTINATIONS
            ? JSON.parse(shiori.DESTINATIONS)
            : [];

          // 各渡航先について
          destinations.forEach((destination) => {
            // 日本語国名からISO国コードに変換
            const isoCode = getISOCode(destination);

            if (isoCode) {
              if (!grouped[isoCode]) {
                grouped[isoCode] = [];
              }
              // 重複を避けるため、同じしおりが既に追加されていないかチェック
              if (
                !grouped[isoCode].some(
                  (s) => s.SHIORI_NOV === shiori.SHIORI_NOV,
                )
              ) {
                grouped[isoCode].push(shiori);
                console.log(
                  `しおり「${shiori.SHIORI_NMV}」を国コード「${isoCode}」(${destination})にグループ化しました`,
                );
              }
            } else {
              console.warn(
                `国名「${destination}」に対応するISO国コードが見つかりません`,
              );
            }
          });
        } catch (error) {
          console.error(
            `しおり「${shiori.SHIORI_NMV}」のDESTINATIONSパースエラー:`,
            error,
          );
        }
      });

      console.log("shioriByCountry グループ化結果:", grouped);
      return grouped;
    });

    /**
     * 国の塗りつぶし色を取得
     * 日本(JP)は特別な色、その他の国はしおりがあれば紫色、なければ白色
     */
    const getCountryFillColor = (countryId) => {
      const hasShiori = shioriByCountry.value[countryId];
      const isHovered = hoveredCountry.value === countryId;

      // 日本の場合は特別な色（緑系）
      if (countryId === "JP") {
        return isHovered ? "#2d7a3e" : "#48bb78"; // 緑色（ホバー時は濃い緑）
      }

      // その他の国
      if (hasShiori) {
        return isHovered ? "#C5DCB3" : "#B4CF9E"; // 紫色（ホバー時は濃い紫）
      }
      return isHovered ? "#f0f0f0" : "#ffffff"; // 白色（ホバー時は薄いグレー）
    };

    /**
     * 国の枠線色を取得
     */
    const getCountryStroke = (countryId) => {
      const isHovered = hoveredCountry.value === countryId;
      return isHovered ? "#333333" : "#cccccc";
    };

    /**
     * 国にマウスホバー時の処理
     */
    const handleCountryHover = (countryId, event) => {
      hoveredCountry.value = countryId;
      updatePopupPosition(event);
    };

    /**
     * ポップアップの位置を更新
     */
    const updatePopupPosition = (event) => {
      popupPosition.value = {
        x: event.clientX,
        y: event.clientY,
      };
    };

    /**
     * 国からマウスが離れた時の処理
     * ポップアップにマウスが移動する場合は閉じない
     */
    const handleCountryLeave = () => {
      // 少し遅延させて、ポップアップにマウスが移動したかチェック
      setTimeout(() => {
        if (!isPopupHovered.value) {
          hoveredCountry.value = null;
        }
      }, 100);
    };

    /**
     * ポップアップにマウスが入った時の処理
     */
    const keepPopupOpen = () => {
      isPopupHovered.value = true;
    };

    /**
     * ポップアップからマウスが離れた時の処理
     */
    const handlePopupLeave = () => {
      isPopupHovered.value = false;
      hoveredCountry.value = null;
    };

    /**
     * 国をクリックした時の処理
     */
    const handleCountryClick = (countryId) => {
      const shioriList = shioriByCountry.value[countryId];
      if (shioriList && shioriList.length === 1) {
        // しおりが1つだけの場合は直接遷移
        emit("shiori-click", shioriList[0].SHIORI_NOV);
      }
      // 複数ある場合はポップアップから選択してもらう
    };

    /**
     * ポップアップ内のしおりをクリックした時の処理
     */
    const emitShioriClick = (shiori) => {
      emit("shiori-click", shiori.SHIORI_NOV);
      // クリック後にポップアップを閉じる
      hoveredCountry.value = null;
      isPopupHovered.value = false;
    };

    /**
     * ISO国コードから表示用の国名を取得
     * 優先順位: 日本語国名 > 英語国名 > SVGのname属性 > ISO国コード
     */
    const getCountryName = (isoCode) => {
      // 日本語国名を取得
      const japaneseName = getJapaneseName(isoCode);
      if (japaneseName) return japaneseName;

      // 英語国名を取得
      const englishName = getEnglishName(isoCode);
      if (englishName) return englishName;

      // SVGのpath要素のname属性を使用
      const path = svgPaths.value.find((p) => p.id === isoCode);
      if (path && path.name) return path.name;

      // それでもなければISO国コードをそのまま返す
      return isoCode;
    };

    /**
     * 日付フォーマット（YYYY/M/D形式）
     */
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
    };

    onMounted(async () => {
      await destMasterStore.fetchAll();
      parseSVGPaths();
    });

    return {
      svgPaths,
      mapScrollContainer,
      hoveredCountry,
      popupPosition,
      shioriByCountry,
      getCountryFillColor,
      getCountryStroke,
      handleCountryHover,
      updatePopupPosition,
      handleCountryLeave,
      handleCountryClick,
      emitShioriClick,
      getCountryName,
      keepPopupOpen,
      handlePopupLeave,
      formatDate,
    };
  },
};
</script>

<style scoped>
.world-map-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
}

.world-map-svg {
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
}

.country-path {
  cursor: pointer;
  transition: fill 0.2s ease, stroke 0.2s ease;
}

.country-path:hover {
  filter: brightness(0.95);
}

/* ポップアップスタイル（日本地図と統一） */
.country-popup {
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 0;
  z-index: 1000;
  min-width: 250px;
  max-width: 350px;
  pointer-events: none;
}

.popup-header {
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  padding: 0.75rem 1rem;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 12px 12px 0 0;
}

.popup-content {
  padding: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
}

.popup-shiori-item {
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  background: #f7fafc;
  transition: background 0.2s;
  pointer-events: auto;
  cursor: pointer;
}

.popup-shiori-item:hover {
  background: #e2e8f0;
}

.popup-shiori-item:last-child {
  margin-bottom: 0;
}

.popup-shiori-title {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
}

.popup-shiori-date {
  font-size: 0.85rem;
  color: #718096;
}

@media (max-width: 640px) {
  .world-map-container {
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;
  }

  .world-map-svg {
    min-width: 760px;
    flex: 0 0 760px;
  }
}
</style>
